// PostPage.js
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import UserContext from '../../context/UserContext';
import PostGallery from '../common/PostGallery';
import TopBar from '../TopBar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser,
  faEllipsisV, 
  faShareAlt, 
  faHeart, 
  faComment,
  faBookmark
} from '@fortawesome/free-solid-svg-icons';
import ShareModal from '../common/ShareModal';
import './PostPage.css';

const PostPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { userData, isLoading: userContextLoading } = useContext(UserContext);
  const [post, setPost] = useState(null);
  const [userPosts, setUserPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [postOwner, setPostOwner] = useState(null);
  const [error, setError] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharePostData, setSharePostData] = useState(null);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [displayedPosts, setDisplayedPosts] = useState(8);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    const fetchPostAndRelatedContent = async () => {
      try {
        console.log('Fetching post data for ID:', postId);
        const db = getFirestore();
        
        // First, find the post in the central posts collection
        const postsQuery = query(collection(db, 'posts'), where('postId', '==', postId));
        const postSnapshot = await getDocs(postsQuery);
        
        if (postSnapshot.empty) {
          console.error('Post not found with ID:', postId);
          setError('Post not found');
          setIsLoading(false);
          return;
        }

        const postData = postSnapshot.docs[0].data();
        console.log('Post data retrieved:', postData);
        setPost(postData);

        // Fetch post owner's data
        const usersQuery = query(collection(db, 'users'), where('userId', '==', postData.userId));
        const userSnapshot = await getDocs(usersQuery);
        
        if (!userSnapshot.empty) {
          const userData = userSnapshot.docs[0].data();
          console.log('User data retrieved:', userData);
          setPostOwner(userData);

          // Fetch more posts from the same user - using simplified query to avoid index issues
          const userPostsQuery = query(
            collection(db, 'posts'),
            where('userId', '==', userData.userId),
            limit(10)
          );
          const userPostsSnapshot = await getDocs(userPostsQuery);
          
          // Filter out the current post client-side
          const userPostsData = userPostsSnapshot.docs
            .map(doc => doc.data())
            .filter(post => post.postId !== postId);
            
          console.log('Related posts retrieved:', userPostsData.length);
          setUserPosts(userPostsData);
          setHasMorePosts(userPostsData.length >= 8);
        }

      } catch (error) {
        console.error('Error fetching post:', error);
        setError(`Error: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPostAndRelatedContent();
  }, [postId, navigate]);

  // Handle gallery open event
  const handleGalleryOpen = (pswp) => {
    setIsGalleryOpen(true);
    // Listen to the change event to detect when user is near the end
    pswp.on('change', () => {
      const totalSlides = pswp.options.dataSource.length;
      const currentIndex = pswp.currIndex;
      
      // If user is within the last 3 slides, load more
      if (currentIndex >= totalSlides - 3 && hasMorePosts && !loadingMoreRef.current) {
        loadMorePosts();
      }
    });
  };

  // Handle gallery close event
  const handleGalleryClose = () => {
    setIsGalleryOpen(false);
  };

  // Function to load more posts
  const loadMorePosts = async () => {
    if (loadingMoreRef.current || !hasMorePosts || !postOwner?.userId) return;
    
    loadingMoreRef.current = true;
    try {
      setIsLoading(true);
      const newCount = displayedPosts + 8;
      setDisplayedPosts(newCount);
      
      const db = getFirestore();
      const userPostsQuery = query(
        collection(db, 'posts'),
        where('userId', '==', postOwner.userId),
        limit(newCount + 1) // +1 to check if there are more
      );
      const userPostsSnapshot = await getDocs(userPostsQuery);
      
      // Filter out the current post
      const userPostsData = userPostsSnapshot.docs
        .map(doc => doc.data())
        .filter(post => post.postId !== postId);
      
      setUserPosts(userPostsData.slice(0, newCount));
      setHasMorePosts(userPostsData.length > newCount);
    } catch (error) {
      console.error('Error loading more posts:', error);
    } finally {
      setIsLoading(false);
      loadingMoreRef.current = false;
    }
  };

  // Handle sharing post
  const handleSharePost = (post) => {
    setSharePostData({
      ...post,
      type: 'post'
    });
    setShowShareModal(true);
  };

  // Handle editing post
  const handleEditPost = (post) => {
    // Navigate to edit page or open edit modal
    console.log('Edit post:', post);
  };

  // Handle deleting post
  const handleDeletePost = async (postId) => {
    // Implement delete functionality
    console.log('Delete post:', postId);
  };

  // Handle reporting post
  const handleReportPost = (post) => {
    // Implement report functionality
    console.log('Report post:', post);
  };

  // Check if we're on mobile
  const isMobileDevice = () => {
    return (
      typeof window !== 'undefined' && 
      (window.innerWidth <= 768 || 'ontouchstart' in window)
    );
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="post-page-container">
        <TopBar />
        <div className="post-page-content">
          <div className="error-container">
            <h2>Something went wrong</h2>
            <p>{error}</p>
            <button onClick={() => navigate('/')}>Back to Home</button>
          </div>
        </div>
      </div>
    );
  }

  if (!post || !postOwner) {
    return (
      <div className="post-page-container">
        <TopBar />
        <div className="post-page-content">
          <div className="error-container">
            <h2>Post not found</h2>
            <p>The post you're looking for has been removed or doesn't exist.</p>
            <button onClick={() => navigate('/')}>Back to Home</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="post-page-container">
      <TopBar />
      <div className="post-page-content">
        <div className="instagram-post-layout">
          {/* Left side - Post media */}
          <div className="post-media-container">
            {post.mediaType === 'video' ? (
              <video controls src={post.mediaUrl} className="post-media"></video>
            ) : (
              <img src={post.mediaUrl} alt={post.caption || 'Post image'} className="post-media" />
            )}
          </div>
          
          {/* Right side - Post info */}
          <div className="post-info-container">
            {/* Post header with user info */}
            <div className="post-header">
              <div className="header-user-info" onClick={() => navigate(`/${postOwner.userName}`)}>
                <img 
                  src={postOwner.profilePhotoUrl || postOwner.profilePictureUrl} 
                  alt={postOwner.fullName} 
                  className="user-avatar"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/32?text=User';
                  }}
                />
                <div className="user-details">
                  <span className="username">{postOwner.userName}</span>
                  {post.location && <span className="location">{post.location}</span>}
                </div>
              </div>
              <button className="more-options-btn">
                <FontAwesomeIcon icon={faEllipsisV} />
              </button>
            </div>
            
            {/* Post content - comments will go here */}
            <div className="post-comments-container">
              {/* Original caption as first comment */}
              {post.caption && (
                <div className="comment">
                  <img 
                    src={postOwner.profilePhotoUrl || postOwner.profilePictureUrl} 
                    alt={postOwner.userName} 
                    className="comment-avatar"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/24?text=User';
                    }}
                  />
                  <div className="comment-content">
                    <span className="comment-username">{postOwner.userName}</span>
                    <span className="comment-text">{post.caption}</span>
                  </div>
                </div>
              )}
              
              {/* Time ago */}
              <div className="post-time">
                {post.createdAt ? new Date(post.createdAt.seconds * 1000).toLocaleDateString() : ''}
              </div>
            </div>
            
            {/* Post actions */}
            <div className="post-actions">
              <div className="action-buttons">
                <button className="action-btn">
                  <FontAwesomeIcon icon={faHeart} />
                </button>
                <button className="action-btn">
                  <FontAwesomeIcon icon={faComment} />
                </button>
                <button className="action-btn" onClick={() => handleSharePost(post)}>
                  <FontAwesomeIcon icon={faShareAlt} />
                </button>
                <button className="action-btn bookmark">
                  <FontAwesomeIcon icon={faBookmark} />
                </button>
              </div>
              
              {/* Like count */}
              <div className="likes-count">
                {post.likes ? `${post.likes} likes` : '0 likes'}
              </div>
              
              {/* Add comment input */}
              <div className="add-comment">
                <input type="text" placeholder="Add a comment..." />
                <button>Post</button>
              </div>
            </div>
          </div>
        </div>
        
        {/* More posts section using PhotoSwipe Gallery */}
        {userPosts.length > 0 && (
          <div className="more-posts-section">
            <h3>More from {postOwner.userName}</h3>
            <PostGallery 
              posts={userPosts}
              isOwner={userData?.userId === postOwner.userId}
              onEdit={handleEditPost}
              onDelete={handleDeletePost}
              onReport={handleReportPost}
              onShare={handleSharePost}
              isMobile={isMobileDevice()}
              onGalleryOpen={handleGalleryOpen}
              onGalleryClose={handleGalleryClose}
            />
            
            {hasMorePosts && !isGalleryOpen && (
              <button 
                className="load-more-btn"
                onClick={loadMorePosts}
                disabled={isLoading}
              >
                {isLoading ? 'Loading...' : 'Load More'}
              </button>
            )}
          </div>
        )}
      </div>
      
      {/* Share Modal */}
      {showShareModal && sharePostData && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          postData={sharePostData}
        />
      )}
    </div>
  );
};

export default PostPage;